import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import { applyLocalizedCanonical } from "../lib/seoMeta";

export default function TermsPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => { applyLocalizedCanonical("/terms"); }, []);
  useEffect(() => { document.title = `${t("legal.terms")} · MachinePilot`; }, [t, i18n.language]);

  const k = (key) => `legalPages.terms.${key}`;
  const items = (key) => t(k(key), { returnObjects: true }) || [];

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-200">
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <BrandIcon size={32} />
            <div className="font-display font-bold text-slate-100 group-hover:text-blue-400">MachinePilot</div>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1"><ArrowLeft size={14}/> Home</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="terms-page">
        <div className="mb-8">
          <div className="label-tech text-amber-400 mb-2">Legal</div>
          <h1 className="font-display font-bold text-4xl text-slate-100 mb-2 flex items-center gap-3">
            <FileText size={32} className="text-amber-400"/> {t("legal.terms")}
          </h1>
          <p className="text-sm text-slate-500">{t("legalPages.updated", { date: t("legalPages.dateValue") })}</p>
        </div>

        <Sec title={t(k("s1title"))}>
          <p>{t(k("s1body"))}</p>
        </Sec>

        <Sec title={t(k("s2title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s2items").map((it, i) => <li key={`s2-${i}`}>{it}</li>)}
          </ul>
        </Sec>

        <Sec title={t(k("s3title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s3items").map((it, i) => <li key={`s3-${i}`}>{it}</li>)}
          </ul>
        </Sec>

        <Sec title={t(k("s4title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s4items").map((it, i) => <li key={`s4-${i}`}>{it}</li>)}
          </ul>
        </Sec>

        <Sec title={t(k("s5title"))}>
          <p>{t(k("s5body"))}</p>
        </Sec>

        <Sec title={t(k("s6title"))}>
          <p>{t(k("s6body"))}</p>
        </Sec>

        <Sec title={t(k("s7title"))}>
          <p>{t(k("s7body"))}</p>
        </Sec>

        <Sec title={t(k("s8title"))}>
          <p>{t(k("s8body"))}</p>
        </Sec>

        <div className="mt-8 flex gap-3 text-sm text-slate-400">
          <Link to="/privacy" className="hover:text-amber-400">{t("legal.privacy")}</Link>
          <Link to="/cookies" className="hover:text-amber-400">{t("legal.cookies")}</Link>
        </div>
      </main>
    </div>
  );
}

function Sec({ title, children }) {
  return (
    <section className="mb-6 p-5 rounded-lg border border-slate-800 bg-slate-900/40">
      <h2 className="font-display font-semibold text-slate-100 mb-3 text-lg">{title}</h2>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}
